/**
 * How a team change made by someone else reaches this tab (TRA-1576).
 *
 * Team membership is the `groups` claim of the Keycloak access token, and
 * nothing else: the api reads it, and so does the team switcher. When staff
 * create a team or add a member, Keycloak knows at once but every open tab
 * keeps its old token. Two small pieces close that gap without a second way
 * of reading teams: a quiet token refresh when the user comes back to the
 * tab (at most once a minute), and a reload of the team list whenever the
 * token's groups change, which also covers the automatic renewal.
 */

/** The least time between two refreshes triggered by returning to the tab. */
export const RETURN_REFRESH_MIN_INTERVAL_MS = 60_000

interface ReturnRefresherOptions {
  isSignedIn: () => boolean
  refresh: () => Promise<void>
  /** When the token was last obtained, so the first return inside the interval does nothing. */
  startedAt: number
  minIntervalMs?: number
}

export interface ReturnRefresher {
  /** Call when the tab becomes visible or the window gets focus. */
  onReturn: (now: number) => void
}

/**
 * Refresh the token when the user returns to the tab, at most once per
 * interval and only while signed in. A refresh that fails is logged and
 * otherwise ignored: a dead session surfaces as a 401 on the next request,
 * and AuthContext already sends the user back to sign in from there.
 */
export function createReturnRefresher({
  isSignedIn,
  refresh,
  startedAt,
  minIntervalMs = RETURN_REFRESH_MIN_INTERVAL_MS,
}: ReturnRefresherOptions): ReturnRefresher {
  let lastRefreshAt = startedAt
  return {
    onReturn: (now: number): void => {
      if (!isSignedIn()) return
      if (now - lastRefreshAt < minIntervalMs) return
      lastRefreshAt = now
      refresh().catch((error: unknown) => {
        console.warn('Token refresh on return to the tab failed:', error)
      })
    },
  }
}

/** One string per set of group ids, the same for any order. */
export function groupsSignature(groupIds: readonly string[]): string {
  return [...groupIds].sort().join('\n')
}

/**
 * Report whether a signature differs from the one seen on the previous
 * call. The first call only records it, so a fresh mount reports no change.
 */
export function createChangeWatcher(): (signature: string) => boolean {
  let last: string | null = null
  return (signature: string): boolean => {
    const changed = last !== null && last !== signature
    last = signature
    return changed
  }
}
